'use client';

/**
 * Intent History Section
 *
 * @component_role Explorer section type `intent_history`
 * @business_rule Displays prior turns of the current intent_search session.
 *   Read-only view of `intentStore.history`. Clicking a turn calls
 *   `loadTurn(turn)` which restores the cached response + conversation id
 *   so the widget rehydrates without a new BFF call.
 *
 *   Dedicated section (not a shared discovery copy) — intent runs in
 *   parallel to discovery and must not couple to `discoveryStore`.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain discovery (intent)
 */

import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useIntentStore } from '@/lib/stores/intent-store';
import type { ExplorerSectionConfig, LabelsDict } from '@/lib/api/types';

export function IntentHistorySection({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config,
  labels: L,
}: {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}) {
  const history = useIntentStore((s) => s.history);
  const loadTurn = useIntentStore((s) => s.loadTurn);

  if (history.length === 0) return null;

  return (
    <div className="border-t border-border">
      <div className="px-3 py-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {L.history_section_header ?? 'History'}
        </h3>
      </div>
      <div className="py-1">
        {history.map((turn) => (
          <button
            key={turn.id}
            onClick={() => loadTurn(turn)}
            className={cn(
              'w-full flex items-start gap-2 px-3 py-2 text-left',
              'hover:bg-accent/50 transition-colors text-sm',
            )}
          >
            <Clock className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <p className="truncate">{turn.query || '(untitled)'}</p>
              <p className="text-xs text-muted-foreground">
                {turn.resultCount} {L.history_results_suffix ?? 'results'}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
